import { Animate, Message, SocialShare, ViewContext } from 'components/lib'
import { useContext } from 'react'
import { ImageDisplay } from 'ui'
import moment from 'moment'
import { T, useTranslate } from '@tolgee/react'
import { LANDING_PAGE_URL, saveToGallery } from 'helpers/image'
import axios from 'axios'
import { errorToast, successToast } from 'helpers'
import { resolutions } from 'toolComponents/image/models/imageSizes'
import GalleryActions from './GalleryActions'
import { selectClientId, setPending } from 'store/generations'
import { useSelector } from 'react-redux'
import { useDispatch } from 'react-redux'

export default function GalleryList({
  items,
  refreshData,
  setIsShowSelector,
  setSelectedGallery,
  selectedGallery,
  setModalData,
  setNewName,
  handleDeleteGallery,
}) {
  const { t } = useTranslate()
  const context = useContext(ViewContext)

  const showShareModal = (content) => {
    context.modal.show({
      children: (
        <div>
          <p className='text-brand-body !mb-[8px] text-center font-bold'>
            <T keyName='eleo-share-content-modal-text'>Share content in Social Media</T>
          </p>
          <div className='flex h-[40px] justify-center'>
            <SocialShare
              className='flex w-full justify-center'
              description={encodeURI(`Generated by Eleo.ai`)}
              url={`${LANDING_PAGE_URL}${content}`}
            />
          </div>
        </div>
      ),
      modalCardClassNames: '!bg-brand-grey !px-[30px] !py-[20px]',
    })
  }

  const showDeleteItemModal = (data) => {
    context.modal.show({
      children: (
        <Message
          title={<T keyName='eleo-gallery-delete-item'>Do you want to delete this item?</T>}
          buttonText={<T keyName='permanently-delete'>Delete permanently</T>}
          buttonAction={() => handleRemoveItem(data)}
          type='warning'
        />
      ),
      modalCardClassNames: '!bg-transparent !px-[0px] !py-[0px]',
    })
  }

  const handleRemoveItem = async (url) => {
    try {
      await axios.post('/api/gallery/remove-item', { url: url, galleryId: selectedGallery.value })
      await refreshData()
      context.modal.hide()
      successToast(<T keyName='eleo-remove-image-success'>Image removed successfully</T>)
    } catch (err) {
      errorToast(<T keyName='eleo-remove-image-failed'>{err.response.data.message}</T>)
    }
  }

  const options = Object.entries(resolutions)
    .map((item) => item[1])
    .flat()

  const clientId = useSelector(selectClientId)

  return (
    <div className='min-w-0 flex-1'>
      <Animate type='pop'>
        {items.length ? (
          <div className='flex h-full flex-wrap justify-center gap-4 overflow-y-auto overflow-x-clip p-4 pb-40'>
            {items.map((item, index) => (
              <div
                className='group relative min-w-64 max-w-72 last:pb-5'
                key={index}
                title={item.description}
              >
                <div className='truncate text-center text-xl font-medium'>{item.name}</div>
                <div className='mb-2 text-center text-sm'>
                  {moment(item.date_created).format('DD.MM.YYYY h:mm')}
                </div>
                <ImageDisplay
                  image={item.image}
                  name={item.name}
                  showShareModal={showShareModal}
                  imageData={item.data}
                  noupload
                  useCors
                  removeItem={showDeleteItemModal}
                  ViewContext={context}
                  saveToGallery={saveToGallery}
                  disableEdit={selectedGallery.isMediaGallery}
                  // noModify
                  previewProportions={
                    options.find((res) => res.value === item.data?.chosenProportions)?.proportions
                  }
                  handleRename={() => {
                    setNewName(item.name ?? '')
                    setModalData({
                      isVisible: true,
                      title: (
                        <T
                          keyName='eleo-image-rename-modal-title'
                          defaultValue='Update image name'
                        />
                      ),
                      acceptLabel: t('eleo-rename', 'rename'),
                      type: 'rename-image',
                      context: { imageUrl: item.image },
                    })
                  }}
                  handleMove={() =>
                    setModalData({
                      isVisible: true,
                      title: (
                        <T
                          keyName='eleo-image-move-modal-title'
                          defaultValue='Move image to another gallery'
                        />
                      ),
                      acceptLabel: t('eleo-move', 'move'),
                      type: 'move-image',
                      context: { imageUrl: item.image },
                    })
                  }
                  handleChangeDescription={() => {
                    setNewName(item.description ?? '')
                    setModalData({
                      isVisible: true,
                      title: (
                        <T
                          keyName='eleo-image-change-description-modal-title'
                          defaultValue='Change this images description'
                        />
                      ),
                      acceptLabel: t('eleo-accept', 'accept'),
                      type: 'change-description',
                      context: { imageUrl: item.image },
                    })
                  }}
                />
              </div>
            ))}

            {/* Ghost elements for proper display in flex wrap */}
            <div className='w-[288px]' />
            <div className='w-[288px]' />
            <div className='w-[288px]' />
            <div className='w-[288px]' />
            <div className='w-[288px]' />
            <div className='w-[288px]' />
          </div>
        ) : (
          <div className='px-8 py-5 text-2xl font-semibold'>
            <T keyName='red-parrot-empty-gallery-text'>
              Nothing here yet. Upload your images with the button on top of the page
            </T>
          </div>
        )}
      </Animate>

      <GalleryActions
        setIsShowSelector={setIsShowSelector}
        setSelectedGallery={setSelectedGallery}
        selectedGallery={selectedGallery}
        setModalData={setModalData}
        setNewGalleryName={setNewName}
        handleDeleteGallery={handleDeleteGallery}
      />
    </div>
  )
}
